import L from 'leaflet'
import dayjs from 'dayjs'
import { CanvasShip } from '@map-sdk/sdk/model/CanvasShip'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import { GPS } from '@map-sdk/sdk/utils/GPS'
import type { Template } from '@map-sdk/sdk/CanvasShipSymbol'
import type {
  LatLng,
  LatLngExpression,
  LatLngLiteral,
  LatLngTuple,
  Layer,
  Map,
  Point,
  Tooltip,
  TooltipOptions,
} from 'leaflet'

export interface CustomMap extends L.Map {
  mapType?: string
  shipxy_tooltip?: Tooltip | null
  shipxy_tooltip_timer?: ReturnType<typeof setTimeout>
}

interface ShipTemplate {
  name: string
  area: [number, number][]
  centerXY: [number, number]
  headXY: [number, number]
  tailXY: [number, number]
  radius: number
  headLength?: number // 可能不需要这个字段，因为上面没有用到
  length?: number // 如果 length 不一定存在，可以标记为可选
  width?: number // 如果 width 不一定存在，也可以标记为可选
}

export class CanvasShipUtils {
  static shipTemplaterThree = {
    name: 'three',
    area: [
      [0, 0],
      [5, -20],
      [10, 0],
      [0, 0],
    ],
    centerXY: [5, -10],
    headXY: [5, -20],
    headXY2: [5, -35],
    headLength: 15,
    r: 10,
  }
  shipTemplaterArc_noUse = {
    name: 'arc',
    area: [
      [0, 5],
      [5, 0],
      [10, 5],
      [5, 10],
    ],
    centerXY: [5, 5],
    headXY: [5, 0],
    headXY2: [5, -15],
    r: 5,
  }
  static dis = {
    zh_CN: {
      naviStatus: [
        '在航(主机推动)',
        '锚泊',
        '失控',
        '操作受限',
        '吃水受限',
        '靠泊',
        '搁浅',
        '捕捞作业',
        '靠船帆提供动力',
      ],
      shipType: [
        '其他',
        '散货船',
        '杂货船',
        '集装箱船',
        '油船',
        '化学品船',
        'LNG',
        'LPG',
        '滚装船',
        '其他货船',
        '其他油船',
        '客船',
        '渔船',
        '拖轮/引航船',
        '其他',
      ],
      shipTypeAIS: [
        '引航船',
        '搜救船',
        '拖轮',
        '港口供应船',
        '装有防污装置和设备的船舶',
        '执法艇',
        '备用-用于当地船舶的任务分配',
        '备用-用于当地船舶的任务分配',
        '医疗船',
        '符合18号决议(Mob-83)的船舶',
        '捕捞',
        '拖引',
        '拖引并且船长>200m或船宽>25m',
        '疏浚或水下作业',
        '潜水作业',
        '参与军事行动',
        '帆船航行',
        '娱乐船',
        '地效应船',
        '高速船',
        '客船',
        '货船',
        '油轮',
        '其他类型的船舶',
        '集装箱船',
        '其他',
      ],
    },
    en_US: {
      naviStatus: [
        'Under way using engine',
        'At anchor',
        'Not under command',
        'Restricted manoeuvrability',
        'Constrained by her draught',
        'Moored',
        'Aground',
        'Engaged in Fishing',
        'Under way sailing',
      ],
      shipType: [
        'Others',
        'Bulk Carriers',
        'General Cargo Ship',
        'Container Ship',
        'Oil Tanker',
        'Chemical Tanker',
        'LNG Tanker',
        'LPG Tanker',
        'Ro-Ro Cargo Ship',
        'Other Cargo Ships',
        'Other Tankers',
        'Passenger Ship',
        'Fishing Ship',
        'Tug/Pilot vessel',
        'Others',
      ],
      shipTypeAIS: [
        'Pilot vessel',
        'Search and rescue vessel',
        'Tug',
        'Port tender',
        'Vessel with anti-pollution facilities or equipment',
        'Law enforcement boat',
        'Spare-for assignments to local vessel',
        'Spare-for assignments to local vessel',
        'Medical transport',
        'Ship according to Resolution No 18(Mob-83)',
        'Fishing',
        'Towing',
        'Towing and length>200m or breadth>25m',
        'Engaged in dredging or underwater operations',
        'Engaged in diving operations',
        'Enagged in military operations',
        'Sailing',
        'Pleasure craft',
        'WIG',
        'HSC',
        'Passenger ship',
        'Cargo ship',
        'Tanker',
        'Other type of ship',
        'Container',
        'Other',
      ],
    },
    seaArea: {
      '1': { id: '1', name_en: 'Baltic Sea', name_cn: '波罗的海' },
      '2': { id: '2', name_en: 'Kattegat', name_cn: '卡特加特海峡' },
      '3': { id: '3', name_en: 'Skaggerak', name_cn: '斯卡格拉克海峡' },
      '4': { id: '4', name_en: 'North Sea', name_cn: '北海' },
      '5': { id: '5', name_en: 'Greenland Sea', name_cn: '格陵兰海' },
      '6': { id: '6', name_en: 'Norwegian Sea', name_cn: '挪威海' },
      '7': { id: '7', name_en: 'Barentsz Sea', name_cn: '巴伦支海' },
      '8': { id: '8', name_en: 'White Sea', name_cn: '白海' },
      '9': { id: '9', name_en: 'Kara Sea', name_cn: '喀拉海' },
      '10': { id: '10', name_en: 'Laptev Sea', name_cn: '拉普捷夫海' },
      '11': { id: '11', name_en: 'East Siberian Sea', name_cn: '东西伯利亚海' },
      '12': { id: '12', name_en: 'Chukchi Sea', name_cn: '楚科奇海' },
      '13': { id: '13', name_en: 'Beaufort Sea', name_cn: '波弗特海' },
      '14': { id: '14', name_en: 'Northwestern Passages', name_cn: '西北通道' },
      '15': { id: '15', name_en: 'Davis Strait', name_cn: '戴维斯海峡' },
      '16': { id: '16', name_en: 'Hudson Bay', name_cn: '哈得逊湾' },
      '17': { id: '17', name_en: 'Arctic Ocean', name_cn: '北冰洋' },
      '18': {
        id: '18',
        name_en: 'Inner Seas off the West Coast of Scotland',
        name_cn: '苏格兰西海岸的海洋',
      },
      '19': {
        id: '19',
        name_en: "Irish Sea and St. George's Channel'",
        name_cn: '爱尔兰海和圣乔治海峡',
      },
      '20': { id: '20', name_en: 'Bristol Channel', name_cn: '布里斯托尔海峡' },
      '21': { id: '21', name_en: 'English Channel', name_cn: '英吉利海峡' },
      '22': { id: '22', name_en: 'Bay of Biscay', name_cn: '比斯开湾' },
      '23': { id: '23', name_en: 'North Atlantic Ocean', name_cn: '北大西洋' },
      '24': { id: '24', name_en: 'Gulf of St-Lawrence', name_cn: '劳伦斯湾' },
      '25': { id: '25', name_en: 'Bay of Fundy', name_cn: '芬迪湾' },
      '26': { id: '26', name_en: 'Gulf of Mexico', name_cn: '墨西哥湾' },
      '27': { id: '27', name_en: 'Caribbean Sea', name_cn: '加勒比海' },
      '28': { id: '28', name_en: 'Mediterranean Sea', name_cn: '地中海' },
      '29': { id: '29', name_en: 'Sea of Marmara', name_cn: '马尔马拉海' },
      '30': { id: '30', name_en: 'Black Sea', name_cn: '黑海' },
      '31': { id: '31', name_en: 'Sea of Azov', name_cn: '亚速海' },
      '32': { id: '32', name_en: 'South Atlantic Ocean', name_cn: '南大西洋' },
      '33': { id: '33', name_en: 'Rio de La Plata', name_cn: '拉普拉塔河' },
      '34': { id: '34', name_en: 'Gulf of Guinea', name_cn: '几内亚湾' },
      '35': { id: '35', name_en: 'Gulf of Suez', name_cn: '苏伊士湾' },
      '36': { id: '36', name_en: 'Gulf of Aqaba', name_cn: '亚喀巴湾' },
      '37': { id: '37', name_en: 'Red Sea', name_cn: '红海' },
      '38': { id: '38', name_en: 'Gulf of Aden', name_cn: '亚丁湾' },
      '39': { id: '39', name_en: 'Arabian Sea', name_cn: '阿拉伯海' },
      '40': { id: '40', name_en: 'Gulf of Oman', name_cn: '阿曼湾' },
      '41': { id: '41', name_en: 'Persian Gulf', name_cn: '波斯湾' },
      '42': { id: '42', name_en: 'Laccadive Sea', name_cn: '拉克代夫海' },
      '43': { id: '43', name_en: 'Bay of Bengal', name_cn: '孟加拉湾' },
      '44': { id: '44', name_en: 'Andaman or Burma Sea', name_cn: '缅甸海' },
      '45': { id: '45', name_en: 'Indian Ocean', name_cn: '印度洋' },
      '46': { id: '46', name_en: 'Malacca Strait', name_cn: '马六甲海峡' },
      '47': { id: '47', name_en: 'Gulf of Thailand', name_cn: '泰国湾' },
      '48': {
        id: '48',
        name_en: 'East Indian Archipelago (Indonesia)',
        name_cn: '东印度群岛',
      },
      '49': { id: '49', name_en: 'South China Sea', name_cn: '南海' },
      '50': { id: '50', name_en: 'Eastern China Sea', name_cn: '东海' },
      '51': { id: '51', name_en: 'Yellow Sea', name_cn: '黄海' },
      '52': { id: '52', name_en: 'Japan Sea', name_cn: '日本海' },
      '53': { id: '53', name_en: 'Inland Sea', name_cn: '濑户内海' },
      '54': { id: '54', name_en: 'Sea of Okhotsk', name_cn: '鄂霍次克海' },
      '55': { id: '55', name_en: 'Bering Sea', name_cn: '白令海' },
      '56': { id: '56', name_en: 'Philippine Sea', name_cn: '菲律宾海' },
      '57': { id: '57', name_en: 'North Pacific Ocean', name_cn: '北太平洋' },
      '58': { id: '58', name_en: 'Gulf of Alaska', name_cn: '阿拉斯加湾' },
      '59': {
        id: '59',
        name_en: 'The Coastal Waters of Southeast Alaska and British Columbia',
        name_cn: '阿拉斯加东南部和不列颠哥伦比亚省的沿海水域',
      },
      '60': {
        id: '60',
        name_en: 'Gulf of California',
        name_cn: '加利福尼亚湾',
      },
      '61': { id: '61', name_en: 'South Pacific Ocean', name_cn: '南太平洋' },
      '62': {
        id: '62',
        name_en: 'Great Australian Bight',
        name_cn: '大澳大利亚湾',
      },
      '63': { id: '63', name_en: 'Tasman Sea', name_cn: '塔斯曼海' },
      '64': { id: '64', name_en: 'Coral Sea', name_cn: '珊瑚海' },
      '65': { id: '65', name_en: 'Solomon Sea', name_cn: '所罗门海' },
      '66': { id: '66', name_en: 'Bismarck Sea', name_cn: '俾斯麦海' },
      '67': { id: '67', name_en: 'Southern Ocean', name_cn: '南大洋' },
      '120100': { id: '120100', name_en: '', name_cn: '天津市天津城区' },
      '130200': { id: '130200', name_en: '', name_cn: '河北省唐山市' },
      '130300': { id: '130300', name_en: '', name_cn: '河北省秦皇岛市' },
      '130900': { id: '130900', name_en: '', name_cn: '河北省沧州市' },
      '210200': { id: '210200', name_en: '', name_cn: '辽宁省大连市' },
      '210700': { id: '210700', name_en: '', name_cn: '辽宁省锦州市' },
      '210800': { id: '210800', name_en: '', name_cn: '辽宁省营口市' },
      '211100': { id: '211100', name_en: '', name_cn: '辽宁省盘锦市' },
      '211400': { id: '211400', name_en: '', name_cn: '辽宁省葫芦岛市' },
      '310100': { id: '310100', name_en: '', name_cn: '上海市上海城区' },
      '320100': { id: '320100', name_en: '', name_cn: '江苏省南京市' },
      '320200': { id: '320200', name_en: '', name_cn: '江苏省无锡市' },
      '320300': { id: '320300', name_en: '', name_cn: '江苏省徐州市' },
      '320400': { id: '320400', name_en: '', name_cn: '江苏省常州市' },
      '320500': { id: '320500', name_en: '', name_cn: '江苏省苏州市' },
      '320600': { id: '320600', name_en: '', name_cn: '江苏省南通市' },
      '320700': { id: '320700', name_en: '', name_cn: '江苏省连云港市' },
      '320800': { id: '320800', name_en: '', name_cn: '江苏省淮安市' },
      '320900': { id: '320900', name_en: '', name_cn: '江苏省盐城市' },
      '321000': { id: '321000', name_en: '', name_cn: '江苏省扬州市' },
      '321100': { id: '321100', name_en: '', name_cn: '江苏省镇江市' },
      '321200': { id: '321200', name_en: '', name_cn: '江苏省泰州市' },
      '321300': { id: '321300', name_en: '', name_cn: '江苏省宿迁市' },
      '330100': { id: '330100', name_en: '', name_cn: '浙江省杭州市' },
      '330200': { id: '330200', name_en: '', name_cn: '浙江省宁波市' },
      '330300': { id: '330300', name_en: '', name_cn: '浙江省温州市' },
      '330400': { id: '330400', name_en: '', name_cn: '浙江省嘉兴市' },
      '330500': { id: '330500', name_en: '', name_cn: '浙江省湖州市' },
      '330900': { id: '330900', name_en: '', name_cn: '浙江省舟山市' },
      '331000': { id: '331000', name_en: '', name_cn: '浙江省台州市' },
      '340200': { id: '340200', name_en: '', name_cn: '安徽省芜湖市' },
      '340500': { id: '340500', name_en: '', name_cn: '安徽省马鞍山市' },
      '340700': { id: '340700', name_en: '', name_cn: '安徽省铜陵市' },
      '340800': { id: '340800', name_en: '', name_cn: '安徽省安庆市' },
      '341700': { id: '341700', name_en: '', name_cn: '安徽省池州市' },
      '350100': { id: '350100', name_en: '', name_cn: '福建省福州市' },
      '350200': { id: '350200', name_en: '', name_cn: '福建省厦门市' },
      '350300': { id: '350300', name_en: '', name_cn: '福建省莆田市' },
      '350500': { id: '350500', name_en: '', name_cn: '福建省泉州市' },
      '350600': { id: '350600', name_en: '', name_cn: '福建省漳州市' },
      '350900': { id: '350900', name_en: '', name_cn: '福建省宁德市' },
      '360400': { id: '360400', name_en: '', name_cn: '江西省九江市' },
      '370200': { id: '370200', name_en: '', name_cn: '山东省青岛市' },
      '370400': { id: '370400', name_en: '', name_cn: '山东省枣庄市' },
      '370500': { id: '370500', name_en: '', name_cn: '山东省东营市' },
      '370600': { id: '370600', name_en: '', name_cn: '山东省烟台市' },
      '370700': { id: '370700', name_en: '', name_cn: '山东省潍坊市' },
      '371000': { id: '371000', name_en: '', name_cn: '山东省威海市' },
      '371100': { id: '371100', name_en: '', name_cn: '山东省日照市' },
      '371600': { id: '371600', name_en: '', name_cn: '山东省滨州市' },
      '420100': { id: '420100', name_en: '', name_cn: '湖北省武汉市' },
      '420200': { id: '420200', name_en: '', name_cn: '湖北省黄石市' },
      '420500': { id: '420500', name_en: '', name_cn: '湖北省宜昌市' },
      '420700': { id: '420700', name_en: '', name_cn: '湖北省鄂州市' },
      '420900': { id: '420900', name_en: '', name_cn: '湖北省孝感市' },
      '421000': { id: '421000', name_en: '', name_cn: '湖北省荆州市' },
      '421100': { id: '421100', name_en: '', name_cn: '湖北省黄冈市' },
      '421200': { id: '421200', name_en: '', name_cn: '湖北省咸宁市' },
      '422800': {
        id: '422800',
        name_en: '',
        name_cn: '湖北省恩施土家族苗族自治州',
      },
      '430100': { id: '430100', name_en: '', name_cn: '湖南省长沙市' },
      '430600': { id: '430600', name_en: '', name_cn: '湖南省岳阳市' },
      '430700': { id: '430700', name_en: '', name_cn: '湖南省常德市' },
      '430900': { id: '430900', name_en: '', name_cn: '湖南省益阳市' },
      '440100': { id: '440100', name_en: '', name_cn: '广东省广州市' },
      '440300': { id: '440300', name_en: '', name_cn: '广东省深圳市' },
      '440400': { id: '440400', name_en: '', name_cn: '广东省珠海市' },
      '440500': { id: '440500', name_en: '', name_cn: '广东省汕头市' },
      '440600': { id: '440600', name_en: '', name_cn: '广东省佛山市' },
      '440700': { id: '440700', name_en: '', name_cn: '广东省江门市' },
      '440800': { id: '440800', name_en: '', name_cn: '广东省湛江市' },
      '440900': { id: '440900', name_en: '', name_cn: '广东省茂名市' },
      '441200': { id: '441200', name_en: '', name_cn: '广东省肇庆市' },
      '441300': { id: '441300', name_en: '', name_cn: '广东省惠州市' },
      '441500': { id: '441500', name_en: '', name_cn: '广东省汕尾市' },
      '441700': { id: '441700', name_en: '', name_cn: '广东省阳江市' },
      '441800': { id: '441800', name_en: '', name_cn: '广东省清远市' },
      '441900': { id: '441900', name_en: '', name_cn: '广东省东莞市' },
      '442000': { id: '442000', name_en: '', name_cn: '广东省中山市' },
      '445100': { id: '445100', name_en: '', name_cn: '广东省潮州市' },
      '445200': { id: '445200', name_en: '', name_cn: '广东省揭阳市' },
      '445300': { id: '445300', name_en: '', name_cn: '广东省云浮市' },
      '450100': { id: '450100', name_en: '', name_cn: '广西壮族自治区南宁市' },
      '450400': { id: '450400', name_en: '', name_cn: '广西壮族自治区梧州市' },
      '450600': {
        id: '450600',
        name_en: '',
        name_cn: '广西壮族自治区防城港市',
      },
      '450700': { id: '450700', name_en: '', name_cn: '广西壮族自治区钦州市' },
      '450800': { id: '450800', name_en: '', name_cn: '广西壮族自治区贵港市' },
      '460100': { id: '460100', name_en: '', name_cn: '海南省海口市' },
      '460200': { id: '460200', name_en: '', name_cn: '海南省三亚市' },
      '510500': { id: '510500', name_en: '', name_cn: '四川省泸州市' },
      '511500': { id: '511500', name_en: '', name_cn: '四川省宜宾市' },
      '710000': { id: '710000', name_en: '', name_cn: '台湾省' },
      '810000': { id: '810000', name_en: '', name_cn: '香港特别行政区' },
      '820000': { id: '820000', name_en: '', name_cn: '澳门特别行政区' },
    },
  } as any
  static isEmpty = CommUtils.isEmpty
  static getObjType = CommUtils.getObjType
  static YuzhengHaijianBaimingdan = undefined
  /**
   * 特定形状船只的模板对象，其中包含了船只各部位的几何属性（如轮廓坐标、中心坐标、头部坐标、尾部坐标、半径、长度和宽度等）。通过给定的高度和宽度先计算出它们各自的一半并除以缩放因子，然后基于这些计算值来构造船只的轮廓区域和其他相关属性。最后，函数返回这个构建完成的船只模板对象
   * @param height 船体高度
   * @param width 船体宽度
   * @param scale 缩放因子
   * @returns
   */
  static getShipTemplaterNine(
    length: number,
    width: number,
    metersPerPixel: number
  ): ShipTemplate {
    const halfLength = length / metersPerPixel / 2
    const halfWidth = width / metersPerPixel / 2
    const shipTemplater: ShipTemplate = {
      name: 'nine',
      area: [],
      centerXY: [0, 0],
      headXY: [0, 0],
      headLength: 50,
      radius: 0,
      tailXY: [0, 0],
    }

    shipTemplater.area.push(
      [0, -halfLength],
      [halfWidth / 2, -halfLength + (3 * halfWidth) / 4],
      [halfWidth, -halfLength + (9 * halfWidth) / 4],
      [halfWidth, -(-halfLength + halfWidth)],
      [(2 * halfWidth) / 3, -(-halfLength)],
      [(-2 * halfWidth) / 3, -(-halfLength)],
      [-halfWidth, -(-halfLength + halfWidth)],
      [-halfWidth, -halfLength + (9 * halfWidth) / 4],
      [-halfWidth / 2, -halfLength + (3 * halfWidth) / 4],
      [0, -halfLength]
    )

    shipTemplater.centerXY = [0, 0]
    shipTemplater.headXY = [0, -halfLength]
    shipTemplater.tailXY = [0, -(-halfLength)]
    shipTemplater.radius = halfLength
    shipTemplater.length = 2 * halfLength
    shipTemplater.width = 2 * halfWidth

    return shipTemplater
  }
  /**
   * 地图实例、船只数据、缩放阈值和默认模板。函数主要目的是根据地图的缩放级别、船只数据以及屏幕像素与实际米数的比例关系，确定是否应用一种特定的（由 this.getShipTemplaterNine 计算得出的）船只渲染模板，并最终返回一个包含渲染所需信息的模板对象。如果某些条件不满足（例如缩放级别低于阈值、船只数据不完整、长宽比不合适等），则返回默认模板
   * @param mapInstance
   * @param shipData
   * @param zoomThreshold
   * @param defaultTemplate
   * @returns
   */
  static getShipTemplater(
    mapInstance: L.Map,
    shipData: { length: number; width: number },
    zoomThreshold: number,
    defaultTemplate?: {
      name: string
      area: number[][]
      centerXY: number[]
      headXY: number[]
      headXY2: number[]
      headLength: number
      r: number
    }
  ) {
    const initialTemplate: Template = {
      name: 'nine',
      area: [],
      centerXY: [0, 0],
      headXY: [0, 0],
      radius: 0,
    }
    // 如果没有提供默认模板，则使用CanvasShipUtils.shipTemplaterThree
    if (!defaultTemplate) {
      defaultTemplate = CanvasShipUtils.shipTemplaterThree
    }

    if (mapInstance.getZoom() < zoomThreshold) {
      return L.extend(initialTemplate, defaultTemplate)
    }
    // 如果船只尺寸信息不完整，则使用默认模板
    if (!shipData || !shipData.length || !shipData.width) {
      return L.extend(initialTemplate, defaultTemplate)
    }

    const pixelPerMeter = this.meters2pixel(mapInstance)
    // 如果无法计算像素与米的比例，则使用默认模板
    if (pixelPerMeter <= 0) {
      return L.extend(initialTemplate, defaultTemplate)
    }

    const scaledLength = shipData.length / pixelPerMeter
    const scaledWidth = shipData.width / pixelPerMeter

    // 如果船只尺寸比例不符合特定条件，则使用默认模板
    if (scaledLength > 25 && scaledWidth > 3) {
      if (
        scaledLength / scaledWidth < 2 ||
        scaledLength / scaledWidth > 20 ||
        shipData.length > 500
      ) {
        return L.extend(initialTemplate, defaultTemplate)
      }

      const ninePartTemplate = this.getShipTemplaterNine(
        shipData.length,
        shipData.width,
        pixelPerMeter
      )

      // 如果找到了特定的模板，则使用它，否则使用默认模板
      return !ninePartTemplate
        ? L.extend(initialTemplate, defaultTemplate)
        : L.extend(initialTemplate, ninePartTemplate)
    }

    return L.extend(initialTemplate, defaultTemplate)
  }
  /**
   * 接受一个船体模板数据和一个缩放因子，将船体模板的所有坐标属性（包括area、centerXY、headXY以及r）根据缩放因子进行缩放，并返回一个新的船体模板数据。
   * @param shipData
   * @param zoomFactor
   * @returns
   */
  static sizeZoomShipTemplater(shipData: Template, zoomFactor: number) {
    const resizedShip = {
      name: 'nine',
      area: [] as any,
      centerXY: [] as any,
      headXY: [] as any,
      radius: 0,
    }

    if (shipData) {
      resizedShip.name = shipData.name

      for (let i = 0; i < shipData.area.length; i++) {
        resizedShip.area.push([
          shipData.area[i][0] * zoomFactor,
          shipData.area[i][1] * zoomFactor,
        ])
      }

      resizedShip.centerXY.push(
        shipData.centerXY[0] * zoomFactor,
        shipData.centerXY[1] * zoomFactor
      )
      resizedShip.headXY.push(
        shipData.headXY[0] * zoomFactor,
        shipData.headXY[1] * zoomFactor
      )

      if (shipData.radius) {
        resizedShip.radius = shipData.radius * zoomFactor
      }
    }

    return resizedShip
  }

  /**
   * 接受一个点坐标、旋转中心坐标以及旋转角度（以度为单位），返回该点绕着旋转中心旋转指定角度后的坐标。
   * @param point
   * @param origin
   * @param angleInDegrees
   * @returns
   */
  static pointRotate(point: any[], origin: any[], angleInDegrees: number) {
    const x = point[0],
      y = point[1],
      centerX = origin[0],
      centerY = origin[1],
      sinTheta = Math.sin((angleInDegrees * Math.PI) / 180),
      cosTheta = Math.cos((angleInDegrees * Math.PI) / 180)

    return [
      (centerX - x) * cosTheta - (centerY - y) * sinTheta + x,
      (centerX - x) * sinTheta + (centerY - y) * cosTheta + y,
    ]
  }
  static getBoundingBoxArea(latlngs: L.LatLngExpression[]): L.Bounds {
    let minX = 0,
      minY = 0,
      maxX = 0,
      maxY = 0
    for (const [i, latlng] of latlngs.entries()) {
      const lat = latlng as any[0]
      const lng = latlng as any[1]
      if (i === 0) {
        minX = lat
        minY = lng
        maxX = lat
        maxY = lng
      } else {
        if (lat < minX) minX = lat
        if (lng < minY) minY = lng
        if (lat > maxX) maxX = lat
        if (lng > maxY) maxY = lng
      }
    }
    return new L.Bounds(new L.Point(minX, minY), new L.Point(maxX, maxY))
  }
  static getLatLngBoundsBoxArea(
    points: L.LatLngExpression[],
    map?: L.Map
  ): L.LatLngBounds {
    let minLat = 0,
      minLng = 0,
      maxLat = 0,
      maxLng = 0
    for (const [i, point] of points.entries()) {
      let latLng = L.latLng(point)
      if (map) {
        latLng = this.GPSEncryptByMapToLatLng(latLng, map)
      }
      const lat = latLng.lat
      const lng = latLng.lng
      if (i === 0) {
        minLat = lat
        minLng = lng
        maxLat = lat
        maxLng = lng
      } else {
        if (lat < minLat) minLat = lat
        if (lng < minLng) minLng = lng
        if (lat > maxLat) maxLat = lat
        if (lng > maxLng) maxLng = lng
      }
    }
    return new L.LatLngBounds(
      new L.LatLng(minLat, minLng),
      new L.LatLng(maxLat, maxLng)
    )
  }
  static fitBoundsByLatlngs(
    map: L.Map,
    bounds: L.LatLngBoundsExpression,
    options: { timeout?: number; extend: number[] } = {
      timeout: 100,
      extend: [50, 50, 50, 50],
    }
  ): void {
    map.fitBounds(bounds)

    setTimeout(() => {
      const extendedBounds = map.getBounds()
      const ne = extendedBounds.getNorthEast()
      const sw = extendedBounds.getSouthWest()

      const neContainerPoint = map.latLngToContainerPoint(ne)
      const swContainerPoint = map.latLngToContainerPoint(sw)

      const topMargin = neContainerPoint.y - options.extend[0]
      const rightMargin = neContainerPoint.x + options.extend[1]
      const bottomMargin = swContainerPoint.y + options.extend[2]
      const leftMargin = swContainerPoint.x - options.extend[3]

      const northEastExtended = map.containerPointToLatLng(
        L.point(rightMargin, topMargin)
      )
      const southWestExtended = map.containerPointToLatLng(
        L.point(leftMargin, bottomMargin)
      )

      console.log([northEastExtended, southWestExtended])
      // 创建一个新的LatLngBounds对象
      map.fitBounds(L.latLngBounds([northEastExtended, southWestExtended]))
    }, options.timeout)
  }
  // 渲染船舶时使用 已调整
  static getSplitLatLngBounds(
    bounds: L.LatLngBounds,
    zoom: number
  ): {
    bounds: L.LatLngBounds
    offset: number
  }[] {
    const splitLatLngBounds: { bounds: L.LatLngBounds; offset: number }[] = []
    const slat = bounds.getSouthWest().lat
    let slng = bounds.getSouthWest().lng
    const nlat = bounds.getNorthEast().lat
    let nlng = bounds.getNorthEast().lng
    let offset = 0
    if (zoom > 2) {
      if (slng >= -180 && nlng <= 180) {
        offset = 0
        splitLatLngBounds.push({
          bounds: L.latLngBounds(
            new L.LatLng(slat, slng),
            new L.LatLng(nlat, nlng)
          ),
          offset,
        })
      } else if (nlng < -180) {
        offset = -360
        splitLatLngBounds.push({
          bounds: L.latLngBounds(
            new L.LatLng(slat, slng - offset),
            new L.LatLng(nlat, nlng - offset)
          ),
          offset,
        })
      } else if (slng > 180) {
        offset = 360
        splitLatLngBounds.push({
          bounds: L.latLngBounds(
            new L.LatLng(slat, slng - offset),
            new L.LatLng(nlat, nlng - offset)
          ),
          offset,
        })
      } else if (slng < -180 && nlng > -180) {
        offset = -360
        splitLatLngBounds.push({
          bounds: L.latLngBounds(
            new L.LatLng(slat, slng - offset),
            new L.LatLng(nlat, -180 - offset)
          ),
          offset,
        })
        offset = 0
        slng = -180
        splitLatLngBounds.push({
          bounds: L.latLngBounds(
            new L.LatLng(slat, slng),
            new L.LatLng(nlat, nlng)
          ),
          offset,
        })
      } else if (slng < 180 && nlng > 180) {
        offset = 360
        splitLatLngBounds.push({
          bounds: L.latLngBounds(
            new L.LatLng(slat, 180 - offset),
            new L.LatLng(nlat, nlng - offset)
          ),
          offset,
        })
        offset = 0
        nlng = 180
        splitLatLngBounds.push({
          bounds: L.latLngBounds(
            new L.LatLng(slat, slng),
            new L.LatLng(nlat, nlng)
          ),
          offset,
        })
      } else {
        console.log('区域船地图视图范围处理异常')
      }
    }
    return splitLatLngBounds
  }
  static getSplitLatLngBounds180(bounds: L.LatLngBounds): L.LatLngBounds[] {
    const westBoundLat = bounds.getSouthWest().lat
    const westBoundLng = bounds.getSouthWest().lng
    const eastBoundLat = bounds.getNorthEast().lat
    const eastBoundLng = bounds.getNorthEast().lng

    const boundsArray: L.LatLngBounds[] = []
    if (Math.abs(westBoundLng - eastBoundLng) >= 180) {
      boundsArray.push(
        L.latLngBounds(
          new L.LatLng(westBoundLat, -180),
          new L.LatLng(eastBoundLat, westBoundLng)
        ),
        L.latLngBounds(
          new L.LatLng(westBoundLat, eastBoundLng),
          new L.LatLng(eastBoundLat, 180)
        )
      )
    } else {
      boundsArray.push(bounds)
    }
    return boundsArray
  }
  static meters2pixel(map: L.Map): number {
    const halfSizeY = map.getSize().y / 2
    return (
      map.distance(
        map.containerPointToLatLng([0, halfSizeY]),
        map.containerPointToLatLng([100, halfSizeY])
      ) / 100
    )
  }

  static getShipRealCenterPoint(
    point: L.Point,
    ship: any,
    meters2pixel: number
  ): L.Point {
    if (!ship.length || !ship.width) return new L.Point(point.x, point.y)

    const shipLength = ship.length
    const shipWidth = ship.width
    let shipLeft = ship.left || shipWidth / 2
    shipLeft = Math.min(shipLeft, shipWidth / 2)
    let shipTrail = ship.trail || shipLength / 2
    shipTrail = Math.min(shipTrail, shipLength / 2)
    const shipHdg = ship.hdg
    const angle = this.getAngle(shipHdg, ship.sog, ship.cog)
    const halfShipWidthInPixel =
      shipWidth / meters2pixel / 2 - shipLeft / meters2pixel
    const halfShipTrailInPixel =
      shipTrail / meters2pixel - shipLength / meters2pixel / 2
    const rotatedPoint = this.pointRotate(
      [point.x, point.y],
      [point.x + halfShipWidthInPixel, point.y + halfShipTrailInPixel],
      angle
    )

    return new L.Point(rotatedPoint[0], rotatedPoint[1])
  }
  static getMousePos(target: HTMLElement, event: any): [number, number] {
    if ('originalEvent' in event) {
      event = event.originalEvent as any
    }
    const rect = target.getBoundingClientRect()
    // const scaleX = rect.width / target.offsetWidth
    // const scaleY = rect.height / target.offsetHeight
    const scaleX = rect.width / (target as any).width
    const scaleY = rect.height / (target as any).height
    return [
      (event.clientX - rect.left) / scaleX,
      (event.clientY - rect.top) / scaleY,
    ]
  }
  /**
   * 该函数接收一个表示经纬度坐标点的数组 latlng 和一个多边形顶点数组 data，用于判断给定点 latlng 是否在由 data 定义的多边形区域内。
函数通过计算给定点与多边形每条边的交叉数来判断点是否在多边形内部。若交叉数不为零，则说明点位于多边形内。
   * @param latlng
   * @param data
   * @returns
   */
  static LatLngInRegion(
    latlng: [number, number],
    data: Array<[number, number]>
  ): boolean {
    if (!latlng || !data) {
      return false
    }

    let crossCount = 0
    for (let i = 0; i < data.length; i++) {
      const nextIndex = (i + 1) % data.length
      const determinant =
        (data[nextIndex][0] - data[i][0]) * (latlng[1] - data[i][1]) -
        (latlng[0] - data[i][0]) * (data[nextIndex][1] - data[i][1])

      if (data[i][1] <= latlng[1]) {
        if (data[nextIndex][1] > latlng[1] && determinant > 0) {
          crossCount++
        }
      } else {
        if (data[nextIndex][1] <= latlng[1] && determinant < 0) {
          crossCount--
        }
      }
    }

    return crossCount !== 0
  }
  static mouseInArr(
    target: HTMLElement,
    event: MouseEvent,
    regions: any[]
  ): any | null {
    if (regions && regions.length > 0) {
      const mousePos = this.getMousePos(target, event)
      for (let i = 0, len = regions.length; i < len; i++) {
        if (this.LatLngInRegion(mousePos, regions[i].bodyArea)) {
          return regions[i]
        }
      }
    }
    return null
  }
  static calcLabel(
    map: L.Map,
    center: L.Point,
    text: string,
    labelList: any[],
    allowance: number,
    avoidOverlap: boolean,
    font: any,
    imgObj: { imgWidth: number; imgSize: number } = {
      imgWidth: 0,
      imgSize: 18,
    }
  ): any | null {
    text = text || 'S'
    const strLength = CommUtils.calcStrLength(map, text, font)
    const imgTotalWidth = strLength + imgObj.imgWidth
    allowance ||= 20
    return this.askLabelSpace(
      center,
      imgTotalWidth,
      imgObj.imgSize + 2,
      labelList,
      allowance,
      avoidOverlap
    )
  }
  // 标牌要使用
  static askLabelSpace(
    center: L.Point,
    imgWidth: number,
    imgSize: number,
    labelList: any[],
    allowance = 50,
    avoidOverlap: boolean
  ): {
    center: L.Point
    x: L.Point
    l: L.Point
    r: L.Point
    bounds: L.Bounds
    position: number
  } | null {
    let bounds: L.Bounds
    let position = -1
    if (!avoidOverlap) {
      position = 0
      bounds = this._getLabelBound(
        center,
        position,
        imgWidth,
        imgSize,
        allowance
      )
    } else {
      for (let i = 0; i < 8; i++) {
        bounds = this._getLabelBound(center, i, imgWidth, imgSize, allowance)
        let isIntersects = false
        for (const element of labelList) {
          const bound = element.bounds as any
          const boundEntity = new L.Bounds(
            new L.Point(bound.min.x - 20, bound.min.y - 20),
            new L.Point(bound.max.x + 20, bound.max.y + 20)
          )
          if (bounds.intersects(boundEntity)) {
            isIntersects = true
            break
          }
        }
        if (!isIntersects) {
          position = i
          break
        }
      }
    }
    if (position > -1) {
      const left = bounds!.getTopLeft()
      const right = bounds!.getBottomRight()
      return {
        center,
        x: CanvasShipUtils._getLabelAnchor(
          center,
          position,
          imgWidth,
          imgSize,
          allowance
        ),
        l: left,
        r: right,
        bounds: bounds!,
        position,
      }
    }
    return null
  }
  /**
   * 为点上的图形请求可用空间
   * @param existingGraphicsData 已存在的图形及其边界信息数据
   * @param centerPoint 图形中心点坐标
   * @param imageWidth 图片宽度
   * @param imageSize 图片高度
   * @param tolerance 容差值，默认为50
   * @param startingSearchIndex  开始搜索的位置，默认从起始方向开始，表示循环8个方向
   * @returns 如果找到可用空间，则返回包含该空间信息的对象；否则返回null
   */
  static askPointSpace(
    data: Record<string, { bounds: L.LatLngBounds }>,
    point: L.Point,
    imgWidth: number,
    imgSize: number,
    allowance = 50,
    startingSearchIndex = 99
  ): {
    center: L.Point
    x: L.Point
    l: L.Point
    r: L.Point
    bounds: L.Bounds
    position: number
  } | null {
    let bounds: L.Bounds
    let position = -1
    let isOverlaps = false

    let currentIndex = startingSearchIndex || 99
    let endIndex = startingSearchIndex + 1
    if (startingSearchIndex === 99) {
      currentIndex = 0
      endIndex = 8
    }

    for (let i = currentIndex; i < endIndex; i++) {
      bounds = this.getPointBound(point, i, imgWidth, imgSize, allowance)
      for (const key in data) {
        if (bounds.overlaps(data[key].bounds as any)) {
          isOverlaps = true
          break
        }
      }
      if (!isOverlaps) {
        position = i
        break
      }
    }

    if (position > -1) {
      const left = bounds!.getTopLeft()
      const right = bounds!.getBottomRight()
      return {
        center: point,
        x: CanvasShipUtils._getLabelAnchor(
          point,
          position,
          imgWidth,
          imgSize,
          allowance
        ),
        l: left,
        r: right,
        bounds: bounds!,
        position,
      }
    }

    return null
  }
  /**
   * 计算点与图片边界的矩形范围
   * @param point 坐标点
   * @param key 图片位置索引
   * @param imgWidth 图片宽度
   * @param imgSize 图片高度
   * @param allowance 容差值，默认留白空间
   * @returns 返回包含边界信息的L.Bounds对象
   */
  static getPointBound(
    point: L.Point,
    key: number,
    imgWidth: number,
    imgSize: number,
    allowance: number
  ): L.Bounds {
    let x1 = 0,
      x2 = 0,
      y2 = 0,
      y1 = 0
    const pointX = point.x
    const pointY = point.y

    switch (key) {
      case 0:
        x2 = (x1 = pointX + allowance) + imgWidth
        y1 = (y2 = pointY - allowance) - imgSize
        break
      case 1:
        x2 = (x1 = pointX + allowance) + imgWidth
        y1 = (y2 = pointY + imgSize / 2) - imgSize
        break
      case 2:
        x2 = (x1 = pointX + allowance) + imgWidth
        y1 = (y2 = pointY + allowance + imgSize) - imgSize
        break
      case 3:
        x2 = (x1 = pointX - imgWidth / 2) + imgWidth
        y1 = (y2 = pointY + allowance + imgSize) - imgSize
        break
      case 4:
        x1 = (x2 = pointX) - imgWidth
        y1 = (y2 = pointY + allowance + imgSize) - imgSize
        break
      case 5:
        x1 = (x2 = pointX - allowance) - imgWidth
        y1 = (y2 = pointY + imgSize / 2) - imgSize
        break
      case 6:
        x1 = (x2 = pointX) - imgWidth
        y1 = (y2 = pointY - allowance) - imgSize
        break
      case 7:
        x2 = (x1 = pointX - imgWidth / 2) + imgWidth
        y1 = (y2 = pointY - allowance) - imgSize
    }

    return new L.Bounds(new L.Point(x1, y1), new L.Point(x2, y2))
  }

  /**
   * 计算标签的边界范围
   * @param point 坐标点
   * @param key 图片位置索引
   * @param imgWidth 图片宽度
   * @param imgSize 图片高度
   * @param allowance 容差值，默认留白空间
   * @returns 返回包含边界信息的L.Bounds对象
   */
  static _getLabelBound(
    point: L.Point,
    key: number,
    imgWidth: number,
    imgSize: number,
    allowance: number
  ): L.Bounds {
    return this.getPointBound(point, key, imgWidth, imgSize, allowance)
  }

  /**
   * 计算标签锚点的位置
   * @param point 坐标点
   * @param key 图片位置索引
   * @param imgWidth 图片宽度
   * @param imgSize 图片高度
   * @param allowance 容差值，默认留白空间
   * @returns 返回包含锚点信息的L.Point对象
   */
  static _getLabelAnchor(
    point: L.Point,
    key: number,
    imgWidth = 70,
    imgSize = 20,
    allowance = 20
  ): L.Point {
    const anchorPoint = new L.Point(0, 0)

    switch (key) {
      case 0:
        anchorPoint.x = point.x + allowance
        anchorPoint.y = point.y - allowance - imgSize / 2
        break
      case 1:
        anchorPoint.x = point.x + allowance
        anchorPoint.y = point.y
        break
      case 2:
        anchorPoint.x = point.x + allowance
        anchorPoint.y = point.y + allowance + imgSize / 2
        break
      case 3:
        anchorPoint.x = point.x
        anchorPoint.y = point.y + allowance + imgSize / 4
        break
      case 4:
        anchorPoint.x = Math.max(point.x - allowance, point.x - imgWidth / 2)
        anchorPoint.y = point.y + allowance + imgSize / 4
        break
      case 5:
        anchorPoint.x = point.x - allowance
        anchorPoint.y = point.y
        break
      case 6:
        anchorPoint.x = Math.max(point.x - allowance, point.x - imgWidth / 2)
        anchorPoint.y = point.y - allowance - imgSize / 4
        break
      case 7:
        anchorPoint.x = point.x
        anchorPoint.y = point.y - allowance - imgSize / 4
    }

    return anchorPoint
  }
  static calcStrWidthOnCanvas(text: string, font?: string): number {
    if (!text || text.length === 0) {
      return 0
    }
    // 创建并初始化一个临时 canvas 元素
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return 0
    }
    ctx.font = font || 'bold 12px Arial'
    // 计算文本宽度
    const width = ctx.measureText(text).width
    // 无需在地图容器内操作，所以这里省略了与地图容器相关的DOM操作
    return width
  }
  static calcStrLength(map: Map, text: string, font?: string): number {
    const width = this.calcStrWidthOnCanvas(text, font)
    // 这里原本的操作已经简化掉，仅保留计算部分
    return width
  }
  static drawRectangleRadius(
    canvas: CanvasRenderingContext2D,
    x: number,
    y: number,
    xAllowance: number,
    yAllowance: number,
    strokeStyle = '#000000',
    fillStyle?: string
  ): void {
    canvas.save()

    canvas.beginPath()
    canvas.moveTo(x, y)
    canvas.lineTo(x, y + yAllowance)
    canvas.lineTo(x + xAllowance, y + yAllowance)
    canvas.lineTo(x + xAllowance, y)
    canvas.lineTo(x, y)

    canvas.lineWidth = 1
    canvas.lineJoin = 'round'
    if (strokeStyle) {
      canvas.strokeStyle = strokeStyle
    }
    if (fillStyle) {
      canvas.fillStyle = fillStyle
    }

    if (strokeStyle) {
      canvas.stroke()
    }
    if (fillStyle) {
      canvas.fill()
    }

    canvas.closePath()
    canvas.restore()
  }
  static trackUnitConversion(ship: any): Record<string, number> | null {
    const tracks: { [key: string]: any } = {}

    if (!ship) return null

    if (ship.hdg) {
      tracks.hdg = (ship.hdg / 100).toFixed(2)
      if (tracks.hdg > 512) tracks.hdg = undefined
      while (tracks.hdg > 360) tracks.hdg -= 360
    }

    if (ship.cog) {
      tracks.cog = (ship.cog / 100).toFixed(2)
      if (tracks.cog > 512) tracks.cog = undefined
      while (tracks.cog > 360) tracks.cog -= 360
    }

    if (ship.sog) {
      tracks.sog = (ship.sog / 514).toFixed(2)
      if (tracks.sog > 102.2) tracks.sog = undefined
    }

    if (ship.draught) {
      tracks.draught = (ship.draught / 1000).toFixed(1)
    }

    if (ship.rot) {
      if (ship.rot > 1200 || ship.rot < -1200) {
        tracks.rot = undefined
      } else {
        tracks.rot = (ship.rot / 100).toFixed(2)
      }
    }

    return tracks
  }
  static mToUnit(distance: number, unit: string): number | string {
    let convertedDistance: string | number = 0
    if (this.isEmpty(distance)) return convertedDistance

    switch (unit) {
      case 'nm':
      case 'nauticalmiles':
        convertedDistance = (distance / 1852).toFixed(
          distance >= 1852000 ? 0 : distance >= 185200 ? 1 : 2
        )
        break
      case 'mi':
      case 'landmiles':
        convertedDistance = (distance / 1609.344).toFixed(
          distance >= 1609344 ? 0 : distance >= 160934.4 ? 1 : 2
        )
        break
      default:
        convertedDistance = (distance / 1000).toFixed(
          distance >= 1000000 ? 0 : distance >= 186_411 ? 1 : 2
        )
    }

    return Number(convertedDistance)
  }
  static unitToM(distance: number, unit: string): number {
    let meters: number | string = distance
    if (this.isEmpty(distance)) return 0

    switch (unit) {
      case 'nm':
      case 'nauticalmiles':
        meters = (1852 * distance).toFixed(0)
        break
      case 'mi':
      case 'landmiles':
        meters = (1609.344 * distance).toFixed(0)
        break
      case 'km':
        meters = (1000 * distance).toFixed(0)
        break
    }

    return Number(meters)
  }
  static dataToShip(
    data: any,
    options?: any,
    normalizeData?: boolean
  ): CanvasShip {
    normalizeData = normalizeData !== false
    const ship = new CanvasShip()
    L.Util.extend(ship, data)

    ship.type = `${ship.shiptype}`
    if (ship.shiptype && ship.rot && (ship.rot > 1200 || ship.rot < -1200)) {
      ship.rot = 0
    }
    if (normalizeData) {
      ship.lng /= 1000000
      ship.lat /= 1000000
      ship.hdg = CommUtils.getNumberLEValue(ship.hdg, 35900, 100, 1)
      ship.hdg = this.xTo360(ship.hdg)
      ship.cog = CommUtils.getNumberLEValue(ship.cog, 35990, 100, 1)
      ship.cog = this.xTo360(ship.cog)
      ship.sog = CommUtils.getNumberLEValue(ship.sog, 52576, 514, 1)
      ship.rot = ship.rot ? CommUtils.getNumber(ship.rot / 100, 2) : 0
      ship.draught /= 1000
      ship.length /= 10
      ship.width /= 10
      ship.left /= 10
      ship.trail /= 10
    } else {
      if (ship.lng > 1000) {
        ship.lng /= 1000000
      }
      if (ship.lat > 1000) {
        ship.lat /= 1000000
      }
      if (ship.hdg && ship.hdg > 1000) {
        ship.hdg = CommUtils.getNumberLEValue(ship.hdg, 35900, 100, 1)
        ship.hdg = this.xTo360(ship.hdg)
      }
      if (ship.cog && ship.cog > 1000) {
        ship.cog = CommUtils.getNumberLEValue(ship.cog, 35990, 100, 1)
        ship.cog = this.xTo360(ship.cog)
      }
      if (ship.sog && ship.sog > 514) {
        ship.sog = CommUtils.getNumberLEValue(ship.sog, 52576, 514, 1)
      }
      if (ship.rot && ship.rot > 1000) {
        ship.rot = CommUtils.getNumber(ship.rot / 100, 2)
      }
      if (ship.draught > 1000) {
        ship.draught = CommUtils.getNumber(ship.draught / 1000, 1)
      }
    }

    if (options) {
      ship.color = options.color || '#d3d3d3'
      ship.istop = !options.istop || options.istop
    }

    ship.datapower = `${ship.mmsi}`.indexOf('*') > 0 ? 1 : 0
    ship.is_yh = this.isYUNZHENAndHAIJIAN(ship.name, ship.mmsi, ship) || false
    ship.outCN = GPS.outOfChina(ship.lat, ship.lng)

    ship.gps_lat = ship.lat
    ship.gps_lng = ship.lng
    ship.gps_lastdyn = `${ship.lastdyn}`

    return ship
  }
  sortArray(
    sortingArr: any[],
    keyName?: string | boolean,
    ascendingFlag?: boolean
  ): any[] {
    if (!sortingArr || sortingArr.length < 2) {
      return sortingArr
    }

    if (typeof keyName === 'undefined') {
      keyName = true
    }
    if (typeof ascendingFlag === 'undefined') {
      ascendingFlag = true
    }

    if (keyName) {
      return ascendingFlag
        ? sortingArr.sort((a: any, b: any) => {
            if (typeof a === 'string' && typeof b === 'string') {
              return a.localeCompare(b)
            } else {
              return a - b
            }
          })
        : sortingArr.sort((a: any, b: any) => {
            if (a.length === b.length) {
              return a.localeCompare(b)
            } else {
              return a.length - b.length
            }
          })
    } else {
      return ascendingFlag
        ? sortingArr.sort((a: any, b: any) => b - a)
        : sortingArr.sort((a: any, b: any) => {
            if (a.length === b.length) {
              return b.localeCompare(a)
            } else {
              return b.length - a.length
            }
          })
    }
  }
  static getNumber(
    value: any,
    precision?: number,
    roundingMethod?: 'round' | 'floor' | 'ceil'
  ): number {
    let result = 0
    if (!value) {
      return 0
    }

    let multiplier = 10 ** (precision ?? 0)
    multiplier <= 0 && (multiplier = 1)

    switch (roundingMethod) {
      case 'round':
        result = Math.round(value * multiplier) / multiplier
        break
      case 'floor':
      default:
        result = Math.floor(value * multiplier) / multiplier
        break
      case 'ceil':
        result = Math.ceil(value * multiplier) / multiplier
    }

    return result
  }
  static openTooltip(
    mapInstance: CustomMap,
    latlng: LatLngLiteral | LatLngExpression,
    content: string | HTMLElement,
    tooltipOptions: TooltipOptions
  ): L.Tooltip {
    // 关闭当前地图实例上的tooltip
    CanvasShipUtils.closeTooltip(mapInstance)

    // 创建新的Tooltip对象，并将其存储在shipxy_tooltip属性中
    const tooltip = new L.Tooltip(tooltipOptions)
    mapInstance.shipxy_tooltip = tooltip

    // 设置Tooltip的内容
    mapInstance.shipxy_tooltip.setContent(content)

    // 设置Tooltip的位置坐标
    mapInstance.shipxy_tooltip.setLatLng(L.latLng(latlng))

    // 将Tooltip图层添加到地图实例上
    mapInstance.addLayer(mapInstance.shipxy_tooltip)

    // 如果存在shipxy_tooltip_timer，则清除之前的定时器
    if (mapInstance.shipxy_tooltip_timer) {
      clearTimeout(mapInstance.shipxy_tooltip_timer)
    }

    // 设置新的定时器，在5秒后执行回调函数，移除当前的Tooltip
    mapInstance.shipxy_tooltip_timer = setTimeout(
      (tooltipToRemove: Tooltip | undefined) => {
        if (tooltipToRemove) {
          tooltipToRemove.remove()
        }
      },
      5000,
      mapInstance.shipxy_tooltip
    )

    // 返回已创建和显示的Tooltip对象
    return mapInstance.shipxy_tooltip
  }
  static closeTooltip(mapInstance: CustomMap, specificTooltip?: Layer): void {
    if (specificTooltip) {
      mapInstance.removeLayer(specificTooltip)
    }

    const currentTooltip = mapInstance.shipxy_tooltip
    if (currentTooltip) {
      mapInstance.removeLayer(currentTooltip)
      mapInstance.shipxy_tooltip = null
    }
  }
  static dataFormat(
    date: string | number | Date | dayjs.Dayjs | null | undefined,
    format: string | undefined
  ) {
    return dayjs(date).format(format)
  }
  static getGPSLatLngByMap(
    mapCoordinate: LatLngLiteral | LatLngTuple,
    mapParams: any
  ): LatLng {
    return GPS.latLngToGPSBymap(mapCoordinate, mapParams)
  }
  static GPSEncryptByMapToLatLng(latLng: LatLngExpression, map: L.Map): LatLng {
    return GPS.gpsToLatLngBymap(latLng, map as any)
  }
  static GPSEncryptByMapTypeToLatLng(
    latLng: LatLngExpression,
    type: string
  ): LatLng {
    const latLngInstance = L.latLng(latLng)
    if (type === 'MT_ESRI') {
      const { lat, lon } = GPS.gcj_encrypt(
        latLngInstance.lat,
        latLngInstance.lng
      )
      return L.latLng(lat, lon)
    }
    return latLngInstance
  }
  static GPSEncryptByMapTypeToPoint(
    latLng: LatLngExpression,
    mapType: string,
    map: L.Map
  ): Point {
    return this.GPSEncryptByMapToPoint(latLng, map)
  }
  static GPSEncryptByMapToPoint(latLng: LatLngExpression, map: L.Map): Point {
    const encryptedLatLng = this.GPSEncryptByMapToLatLng(latLng, map)
    return map.latLngToContainerPoint(encryptedLatLng)
  }
  static latFormatter(lat: number): string {
    if (!lat || `${lat}`.length === 0) return ''

    const direction = lat < 0 ? ' S' : ' N'
    let formattedLat = String(lat)
    if (-1 === formattedLat.lastIndexOf('.')) {
      formattedLat += '.000'
    }
    const decimalIndex = formattedLat.lastIndexOf('.')
    const degrees = Number(formattedLat.slice(0, Math.max(0, decimalIndex)))
    const minutes =
      60 *
      Number(
        `0.${formattedLat.substring(decimalIndex + 1, formattedLat.length)}`
      )

    return `${Math.abs(degrees)}º ${L.Util.formatNum(minutes, 3)}${direction}`
  }
  static lngFormatter(lng: number): string {
    if (!lng || `${lng}`.length === 0) return ''

    let direction = 'W'
    if (((lng %= 360) < 0 && lng > -180) || (lng > 180 && lng < 360)) {
      direction = ' W'
      if (lng > 180 && lng < 360) {
        lng -= 360
      }
    } else if ((lng > 0 && lng < 180) || (lng < -180 && lng > -360)) {
      direction = ' E'
      if (lng < -180 && lng > -360) {
        lng += 360
      }
    }

    let formattedLng = String(lng)
    if (-1 === formattedLng.lastIndexOf('.')) {
      formattedLng += '.000'
    }
    const decimalIndex = formattedLng.lastIndexOf('.')
    const degrees = Number(formattedLng.slice(0, Math.max(0, decimalIndex)))
    const minutes =
      60 *
      Number(
        `0.${formattedLng.substring(decimalIndex + 1, formattedLng.length)}`
      )

    return `${Math.abs(degrees)}º ${L.Util.formatNum(minutes, 3)}${direction}`
  }
  static latlngFormatter(latlng: L.LatLngLiteral | L.LatLng): string {
    if (!latlng) return ''
    const { lat, lng } = L.latLng(latlng as L.LatLngLiteral)
    return `${this.latFormatter(lat)},${this.lngFormatter(lng)}`
  }
  /**
   * 格式化鼠标悬浮经纬度
   * @param value
   * @param isLng 参数来区分经度和纬度的处理方式
   * @returns
   */
  static formatDegreesToDMS(value: number, isLng: boolean): string {
    const direction: 'E' | 'W' | 'N' | 'S' = isLng
      ? value > 0
        ? 'E'
        : 'W'
      : value > 0
      ? 'N'
      : 'S'
    value = ((value + 180) % 360) - (isLng && value > 0 ? 360 : 0)

    const degreeStr = String(value)
    const decimalIndex = degreeStr.lastIndexOf('.')
    const degrees = Math.abs(
      Number(degreeStr.slice(0, Math.max(0, decimalIndex)))
    )
    const minutes =
      Number(`0.${degreeStr.slice(Math.max(0, decimalIndex + 1))}`) * 60

    return `${degrees}º ${L.Util.formatNum(minutes, 3)}${direction}`
  }

  static isLatLngInRegion(
    latlng: [number, number],
    data: [number, number][]
  ): boolean {
    if (!latlng || !data) {
      return false
    }

    let counter = 0
    let nextIndex = 0

    for (let i = 0; i < data.length; i++) {
      const lastIndex = data.length - 1
      nextIndex = i === lastIndex ? 0 : i + 1

      const crossProduct =
        (data[nextIndex][0] - data[i][0]) * (latlng[1] - data[i][1]) -
        (latlng[0] - data[i][0]) * (data[nextIndex][1] - data[i][1])

      if (data[i][1] <= latlng[1]) {
        if (data[nextIndex][1] > latlng[1] && crossProduct > 0) {
          counter++
        }
      } else {
        if (data[nextIndex][1] <= latlng[1] && crossProduct < 0) {
          counter--
        }
      }
    }

    return counter !== 0
  }

  static getAngleByLatLng(
    latLng: L.LatLngExpression,
    latLng1: L.LatLngExpression
  ): number {
    const pointA = L.latLng(latLng)
    const pointB = L.latLng(latLng1)
    const deltaLat = pointB.lat - pointA.lat
    const deltaLng = pointB.lng - pointA.lng
    return Number((57.29578 * -Math.atan2(deltaLat, deltaLng)).toFixed(2))
  }
  static getAngleByLatLngToBigCircle(
    beforeLatLng: L.LatLngExpression,
    latLng: L.LatLngExpression
  ): number {
    // 使用Leaflet的latLng方法来确保输入是有效的经纬度对象
    const beforePoint = L.latLng(beforeLatLng)
    const targetPoint = L.latLng(latLng)

    // 将经纬度从度转换为弧度
    const beforeLatRad = beforePoint.lat * (Math.PI / 180)
    const beforeLngRad = beforePoint.lng * (Math.PI / 180)
    const latRad = targetPoint.lat * (Math.PI / 180)
    const lngRad = targetPoint.lng * (Math.PI / 180)

    // 使用球面三角学公式计算两点之间的方位角
    const sinDeltaLng = Math.sin(lngRad - beforeLngRad)
    const cosLat1 = Math.cos(beforeLatRad)
    const sinLat2 = Math.sin(latRad)
    const cosLat2 = Math.cos(latRad)
    const sinDeltaLat =
      cosLat1 * sinLat2 -
      Math.sin(beforeLatRad) * cosLat2 * Math.cos(lngRad - beforeLngRad)

    // 计算方位角，并将结果从弧度转换为度
    const bearing = Math.atan2(sinDeltaLng, sinDeltaLat) * (180 / Math.PI)

    // 调整方位角，确保它在0到360度之间
    const adjustedBearing = Number(bearing.toFixed(2)) + (bearing < 0 ? 360 : 0)

    // 返回调整后的方位角，保留两位小数
    return adjustedBearing
  }
  static getAngleForLatLngByDistFromLatLngs(
    startLatLng: L.LatLngExpression,
    endLatLng: L.LatLngExpression
  ): number {
    // 使用 Leaflet 的 latLng 方法将输入转换为经纬度对象
    const startPoint = L.latLng(startLatLng)
    const endPoint = L.latLng(endLatLng)

    // 将经纬度从度转换为弧度
    const startLatRad = startPoint.lat * (Math.PI / 180)
    const startLngRad = startPoint.lng * (Math.PI / 180)
    const endLatRad = endPoint.lat * (Math.PI / 180)
    const endLngRad = endPoint.lng * (Math.PI / 180)

    // 使用 Haversine 公式计算两点之间的大圆距离
    const dLat = endLatRad - startLatRad
    const dLng = endLngRad - startLngRad

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(startLatRad) * Math.cos(endLatRad) * Math.sin(dLng / 2) ** 2
    // 返回弧度表示的角度
    return 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }
  static getLatLngByDistFromLatLngs(
    startLatLng: L.LatLng,
    endLatLng: L.LatLng,
    distanceRatio: number,
    distance: number
  ): L.LatLng | null {
    if (
      !(startLatLng instanceof L.LatLng) ||
      !(endLatLng instanceof L.LatLng) ||
      distanceRatio < 0 ||
      distanceRatio > 1 ||
      distance <= 0
    ) {
      return null
    }

    const angle =
      distanceRatio ||
      this.getAngleForLatLngByDistFromLatLngs(startLatLng, endLatLng)

    if (angle === 0 || distance < 1) {
      return null
    }

    const distanceToEachOther = startLatLng.distanceTo(endLatLng)
    const distanceFraction = distance / distanceToEachOther

    const startLatRad = startLatLng.lat * (Math.PI / 180)
    const startLngRad = startLatLng.lng * (Math.PI / 180)
    const endLatRad = endLatLng.lat * (Math.PI / 180)
    const endLngRad = endLatLng.lng * (Math.PI / 180)

    const sinFraction = Math.sin((1 - distanceFraction) * angle)
    const cosFraction = Math.cos(distanceFraction * angle)

    const x =
      sinFraction * Math.cos(startLatRad) * Math.cos(startLngRad) +
      cosFraction * Math.cos(endLatRad) * Math.cos(endLngRad)
    const y =
      sinFraction * Math.cos(startLatRad) * Math.sin(startLngRad) +
      cosFraction * Math.cos(endLatRad) * Math.sin(endLngRad)
    const z =
      sinFraction * Math.sin(startLatRad) + cosFraction * Math.sin(endLatRad)

    const newLatRad = Math.atan2(z, Math.sqrt(x ** 2 + y ** 2))
    const newLngRad = Math.atan2(y, x)

    const newLatitude = (newLatRad * (180 / Math.PI)) as any // as any是为了绕过TypeScript的类型检查，因为Math.atan2可能返回-π到π之间的值，需要转换为-90到90之间的纬度值
    const newLongitude = (newLngRad * (180 / Math.PI)) as any // 同上，需要转换为-180到180之间的经度值

    // 规范化经度，使其处于-180到180之间
    let normalizedLongitude = newLongitude - (180 * startLngRad) / Math.PI
    function mathMethod(number: number): number {
      return number > 0 ? Math.floor(number) : Math.ceil(number)
    }
    normalizedLongitude -=
      normalizedLongitude < 0
        ? 360 * mathMethod((normalizedLongitude - 180) / 360)
        : 360 * mathMethod((normalizedLongitude + 180) / 360)

    return L.latLng(newLatitude, normalizedLongitude)
  }
  static getDistance(
    latLng1: [number, number],
    latLng2: [number, number],
    unitConversionFactor: string
  ): number {
    const point1 = L.latLng(latLng1)
    const point2 = L.latLng(latLng2)

    const lat1InRadians = (point1.lat * Math.PI) / 180
    const lng1InRadians = (point1.lng * Math.PI) / 180
    const lat2InRadians = (point2.lat * Math.PI) / 180
    const lng2InRadians = (point2.lng * Math.PI) / 180

    const deltaLatInRadians = Math.abs(lat1InRadians - lat2InRadians)
    const deltaLngInRadians = Math.abs(lng1InRadians - lng2InRadians)

    const a = Math.sin(deltaLatInRadians / 2)
    const b = Math.sin(deltaLngInRadians / 2)

    const c =
      a ** 2 + Math.cos(lat1InRadians) * Math.cos(lat2InRadians) * b ** 2

    const distanceInMeters = 12742 * Math.asin(Math.sqrt(c)) * 1000

    // 假设 this.mToUnit 是一个将米转换为指定单位的函数
    const distanceInUnits = this.mToUnit(distanceInMeters, unitConversionFactor)

    return Number(distanceInUnits)
  }
  //用于在两个经纬度点之间创建一个平滑的大圆弧线段，并将其分割成多个点。calculateCoordinates 是一个内部辅助函数，用于计算特定比例位置处的弧线上点的经度和纬度。最后，这个函数返回的是由这些点构成的一个数组，每个元素是一个 [经度, 纬度] 对
  static polylineArc(
    item: any,
    nextItem: any,
    resolution?: number
  ): [number, number][] {
    function calculateCoordinates(
      t: number,
      startLat: number,
      startLng: number,
      endLat: number,
      endLng: number,
      arcRadius: number
    ): [number, number] {
      const sinFactor1 = Math.sin((1 - t) * arcRadius) / Math.sin(arcRadius),
        sinFactor2 = Math.sin(t * arcRadius) / Math.sin(arcRadius)
      const x =
          sinFactor1 * Math.cos(startLat) * Math.cos(startLng) +
          sinFactor2 * Math.cos(endLat) * Math.cos(endLng),
        y =
          sinFactor1 * Math.cos(startLat) * Math.sin(startLng) +
          sinFactor2 * Math.cos(endLat) * Math.sin(endLng),
        z = sinFactor1 * Math.sin(startLat) + sinFactor2 * Math.sin(endLat)

      const bearing = (180 / Math.PI) * Math.atan2(z, Math.sqrt(x * x + y * y)),
        inclination = (180 / Math.PI) * Math.atan2(y, x),
        adjustedInclination = inclination - (180 * startLng) / Math.PI

      function roundIfPositive(value: number): number {
        return Math[value > 0 ? 'floor' : 'ceil'](value)
      }

      return [
        bearing,
        (inclination -
          (adjustedInclination < 0
            ? 360 * roundIfPositive((adjustedInclination - 180) / 360)
            : 360 * roundIfPositive((adjustedInclination + 180) / 360))) %
          360,
      ]
    }

    // 将 item 和 nextItem 转换成 Leaflet LatLng 对象
    item = L.latLng(item)
    nextItem = L.latLng(nextItem)

    let startLat = item.lat,
      startLng = item.lng,
      endLat = nextItem.lat,
      endLng = nextItem.lng

    // 将经纬度转成弧度
    startLat = (startLat * Math.PI) / 180
    startLng = (startLng * Math.PI) / 180
    endLat = (endLat * Math.PI) / 180
    endLng = (endLng * Math.PI) / 180

    // 计算两点之间的大圆弧长（以弧度表示）
    const greatCircleDistance =
      2 *
      Math.asin(
        Math.sqrt(
          Math.sin((startLat - endLat) / 2) ** 2 +
            Math.cos(startLat) *
              Math.cos(endLat) *
              Math.sin((startLng - endLng) / 2) ** 2
        )
      )

    // 如果距离为0，则直接返回起点坐标
    let resultArray: [number, number][] = []
    if (greatCircleDistance === 0) {
      resultArray = [[startLat, startLng]]
    } else {
      // 否则计算路径上的点
      const segments = resolution || 456 // 默认值为 450
      const segmentRatio = 1 / (segments - 1)

      for (let i = 0; i < segments; i++) {
        const currentRatio = segmentRatio * i
        const coordinates = calculateCoordinates(
          currentRatio,
          startLat,
          startLng,
          endLat,
          endLng,
          greatCircleDistance
        )
        resultArray.push(coordinates)
      }
    }

    return resultArray
  }
  static getAngle(hdg: number, sog: number, cog: number): number {
    let result: string | number = 0
    if (hdg >= 0 && hdg < 360) {
      result =
        sog >= 3 && this.angleAbs(hdg - cog) >= 15 && cog >= 0 && cog < 360
          ? Number(cog).toFixed(5)
          : Number(hdg).toFixed(5)
    } else if (cog >= 0 && cog < 360) {
      result = Number(cog).toFixed(5)
    }
    !result && (result = 0)
    return Number(result)
  }
  static angleAbs(val: number): number {
    const absoluteValue = Math.abs(val)
    return absoluteValue > 180 ? 360 - absoluteValue : absoluteValue
  }

  static lngTo360(val: any): number {
    if (CommUtils.isEmpty(val)) return val
    val = Number(val)
    while (val < 0) val += 360
    return val
  }
  static lngTo360ForArray(
    inputArray: { lon?: number; lng?: number }[]
  ): { lon?: number; lng?: number }[] {
    const outputArray: { lon?: number; lng?: number }[] = []

    if (!inputArray || inputArray.length === 0) {
      return outputArray
    }

    for (const item of inputArray) {
      if (!item.lng) {
        item.lng = item.lon // 如果 lng 属性不存在，则使用 lon 属性的值
      }
      item.lng = Number(item.lng) // 确保 lng 是一个数字

      if (item.lng < 0) {
        item.lng = this.lngTo360(item.lng) // 如果 lng 小于 0，则调用 lngTo360 函数进行转换
      }

      if (item.lon) {
        item.lon = item.lng // 如果 lon 属性存在，则将其值设置为与 lng 相同
      }

      outputArray.push(item) // 将处理过的对象添加到输出数组中
    }

    return outputArray
  }
  static latlngsFrom180(points: any[]): any[] {
    return this.objFrom180(points, '')
  }
  static objFrom180(
    points: any[] & { [key: string]: any },
    propertyPath?: string,
    correction?: number
  ): any[] {
    if (CommUtils.isEmpty(correction)) correction = 0
    if (!points || points.length < 2) return points

    const objFrom180ToLine = this.objFrom180ToLine(points, propertyPath)
    const length = objFrom180ToLine.length
    let cross180Detected = false,
      moreThan180Count = 0,
      lessThanMinus90Count = 0

    for (let i = 0; i < length; i++) {
      const value = !propertyPath
        ? objFrom180ToLine[i]
        : objFrom180ToLine[i][propertyPath]
      value.lat = Number(value.lat)
      if (CommUtils.isEmpty(value.lng)) value.lng = Number(value.lon)

      if (Math.abs(value.lng) >= 180) cross180Detected = true
      if (value.lng > 360) moreThan180Count++
      if (value.lng <= -90) lessThanMinus90Count++
    }

    if (
      cross180Detected ||
      (correction === 0 && lessThanMinus90Count / length >= 0.5)
    ) {
      let shiftAmount = 0
      if (correction === 0) {
        if (moreThan180Count / length >= 0.5) shiftAmount = -360
        if (lessThanMinus90Count / length >= 0.5) shiftAmount = 360

        for (let i = 0; i < length; i++) {
          const value = !propertyPath
            ? objFrom180ToLine[i]
            : objFrom180ToLine[i][propertyPath]
          value.lng = Number(value.lng) + shiftAmount
          if (!CommUtils.isEmpty(value.lon)) value.lon = value.lng
          if (!propertyPath) {
            objFrom180ToLine[i] = value
          } else {
            objFrom180ToLine[i][propertyPath] = value
          }
        }
      } else if (correction) {
        return objFrom180ToLine
      }
    }

    return objFrom180ToLine
  }

  static objFrom180ToLine(
    points: any[] & { [key: string]: any },
    propertyPath?: string
  ): any[] {
    if (!points) return points
    const copyOfPoints = [...points]

    for (let i = 1; i < copyOfPoints.length; i++) {
      const prevValue = propertyPath
        ? copyOfPoints[i - 1][propertyPath]
        : copyOfPoints[i - 1]
      const currentValue = propertyPath
        ? copyOfPoints[i][propertyPath]
        : copyOfPoints[i]

      prevValue.lat = Number(prevValue.lat)
      if (CommUtils.isEmpty(prevValue.lng)) {
        prevValue.lng = Number(prevValue.lon || prevValue.lng)
      }
      currentValue.lat = Number(currentValue.lat)
      if (CommUtils.isEmpty(currentValue.lng)) {
        currentValue.lng = Number(currentValue.lon || currentValue.lng)
      }

      let shiftAmount = 0
      if (
        Math.abs(
          Number.parseFloat(prevValue.lng) - Number.parseFloat(currentValue.lng)
        ) >= 180
      ) {
        shiftAmount = prevValue.lng >= 0 ? 360 : -360
        currentValue.lng = Number(currentValue.lng) + shiftAmount
        if (!CommUtils.isEmpty(currentValue.lon)) {
          currentValue.lon = currentValue.lng
        }

        if (!propertyPath) {
          copyOfPoints[i - 1] = prevValue
          copyOfPoints[i] = currentValue
        } else {
          copyOfPoints[i - 1][propertyPath] = prevValue
          copyOfPoints[i][propertyPath] = currentValue
        }
      }
    }

    return copyOfPoints
  }
  static lngTo180(lng: any): number {
    if (CommUtils.isEmpty(lng)) return lng
    while (lng > 180) lng -= 360
    while (lng < -180) lng += 360
    return lng
  }
  static latlngsTo180(latlngs: L.LatLng[]): L.LatLngLiteral[] {
    if (!latlngs || latlngs.length < 1) return latlngs

    const length = latlngs.length,
      convertedLatLngs: L.LatLngLiteral[] = []

    for (let i = 0; i < length; i++) {
      const convertedObject: L.LatLng = latlngs[i]
      convertedObject.lng = this.lngTo180(convertedObject.lng)
      convertedLatLngs.push(convertedObject)
    }

    return convertedLatLngs
  }

  static xTo360(val: any): number {
    if (CommUtils.isEmpty(val)) return val
    for (val = Number(val); val > 360; ) val -= 360
    for (; val < -360; ) val += 360
    return val
  }
  static getMaxXYByLatlngs(
    map: L.Map,
    layerLatLng: L.LatLngExpression,
    latLng: L.LatLngExpression
  ): number {
    const distanceXY = this.getDistanceXYByLatlngs(map, layerLatLng, latLng)
    return Math.max(distanceXY.x, distanceXY.y)
  }
  static getDistanceXYByLatlngs(
    map: L.Map,
    layerLatLng: L.LatLngExpression,
    latLng: L.LatLngExpression
  ): { x: number; y: number } {
    const point1 = map.latLngToLayerPoint(layerLatLng)
    const point2 = map.latLngToLayerPoint(latLng)
    return {
      x: Math.abs(point1.x - point2.x),
      y: Math.abs(point1.y - point2.y),
    }
  }

  static getUrlParam(url: string): { [key: string]: string } {
    const queryObject: { [key: string]: string } = {}
    const paramsstartingSearchIndex = url.indexOf('?')
    url = url.slice(paramsstartingSearchIndex + 1)
    const parameters = url.split('&')

    for (const parameter of parameters) {
      const paramIndex = parameter.indexOf('=')
      if (paramIndex > 0) {
        const paramName = parameter.slice(0, Math.max(0, paramIndex))
        const paramValue = parameter.slice(paramIndex + 1)
        queryObject[paramName] = decodeURIComponent(paramValue)
      }
    }

    return queryObject
  }

  static isCNShip(mmsi: string) {
    let countryCode = 1
    const mmsiStr = `${mmsi}`
    if (!this.isEmpty(mmsiStr) && mmsiStr.length >= 3) {
      switch (mmsiStr.slice(0, 3)) {
        case '412':
        case '413':
        case '414':
          countryCode = 0
          break
        case '416':
        case '453':
        case '477':
          countryCode = 2
          break
        default:
          countryCode = 1
      }
    }

    return countryCode
  }
  static isYUNZHENAndHAIJIAN(
    shipName: string,
    shipMMSI: string,
    ship: any
  ): boolean {
    if (!this.isEmpty(shipMMSI)) {
      if (
        !this.YuzhengHaijianBaimingdan &&
        !this.isEmpty(ShipxyOptions.shipDataServer.getYuzhengHaijianBaimingdan)
      ) {
        try {
          //     this.YuzhengHaijianBaimingdan = resData
        } catch (e) {
          console.error('获取运政海船监MMSI白名单异常', e)
        }
      }

      return true
      // if (
      //   this.YuzhengHaijianBaimingdan &&
      //   shipMMSI in this.YuzhengHaijianBaimingdan
      // ) {
      //   return false
      // }
    }

    if (!this.isEmpty(ship) && ship.shiptype !== 0x37) {
      return false
    }

    if (!this.isEmpty(shipName)) {
      shipName = shipName.toUpperCase()
      const shipNameReplace = shipName.replace(/\s+|\d+/gi, '')
      if (CommUtils.isEmpty(shipNameReplace)) {
        return false
      }
      const furtherCleanedName = shipNameReplace.replace(
        /ZHONGGUO|HAIJING|YUZHENG|HAO|HAIJIAN/gi,
        ''
      )
      if (CommUtils.isEmpty(furtherCleanedName)) {
        return true
      }
    }

    return false
  }

  /**
   * 获取由经纬度生成的网格键，默认网格大小为 1，前缀为空字符串，连接符为下划线 _。
   * @param lat 纬度
   * @param lng 经度
   * @param gridSize 网格大小，默认为 1
   * @param prefix 前缀，默认为空字符串
   * @param linkStr 连接符，默认为下划线 _
   * @returns 网格键字符串
   */
  static getGridKeyByLatLng(
    lat: number,
    lng: number,
    gridSize = 1,
    prefix = '',
    linkStr = '_'
  ): string {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return ''
    }
    // 计算网格精度
    const gridKeyPrecision =
      gridSize % 1 !== 0 ? gridSize.toString().split('.')[1]?.length || 0 : 0

    // return (
    //   prefix +
    //   (parseInt((lat / gridSize).toString()) * gridSize).toFixed(
    //     gridKeyPrecision
    //   ) +
    //   linkStr +
    //   (parseInt((lng / gridSize).toString()) * gridSize).toFixed(
    //     gridKeyPrecision
    //   )
    // )
    // 四舍五入经纬度到 gridSize 的整数倍并保留精度
    const roundedLat = (Math.round(lat / gridSize) * gridSize).toFixed(
      gridKeyPrecision
    )
    const roundedLng = (Math.round(lng / gridSize) * gridSize).toFixed(
      gridKeyPrecision
    )

    return prefix + roundedLat + linkStr + roundedLng
  }
  static openLoadingPopup(time: number, cb: any) {
    console.log('打开')
    cb && cb()
  }
  static closeLoadingPopup() {
    console.log('关闭加载框')
  }
  /**
   * 据给定的 key、距离计算方法 disMethod 和语言环境 language 返回对应的描述信息
   * @param key
   * @param disMethod
   * @param language
   * @returns
   */
  static getDisValue(
    key: string | number,
    disMethod: string,
    language: 'zh_CN' | string = 'zh_CN'
  ): string {
    let value = ''

    if (!disMethod || disMethod.length === 0 || Number(key) < 0) {
      return value
    }

    if (disMethod === 'seaArea') {
      const item = this.dis[disMethod][`${key}`]
      if (!item) {
        return ''
      }
      value = language === 'zh_CN' ? item.name_cn : item.name_en
    } else if (language in this.dis && disMethod in this.dis[language]) {
      value = this.dis[language][disMethod][key]
    }

    return value
  }
  static getDisValueEN(
    key: number,
    disMethod: string,
    language: 'en_US'
  ): string {
    if (!disMethod) {
      throw new Error('Invalid disMethod')
    }

    if (100 === key) {
      return this.getDisValue(24, 'shipTypeAIS', language)
    }

    if (key < 10 || key > 100) {
      return this.getDisValue(25, 'shipTypeAIS', language)
    }

    const tensDigit = Math.floor(key / 10)
    const onesDigit = key % 10

    if (5 === tensDigit) {
      return this.getDisValue(onesDigit, 'shipTypeAIS', language)
    }

    if (3 === tensDigit && onesDigit >= 0 && onesDigit <= 7) {
      return this.getDisValue(onesDigit + 10, 'shipTypeAIS', language)
    }

    switch (tensDigit) {
      case 1:
      default:
        return this.getDisValue(25, 'shipTypeAIS', language)
      case 2:
        return this.getDisValue(18, 'shipTypeAIS', language)
      case 4:
        return this.getDisValue(19, 'shipTypeAIS', 'en_US')
      case 6:
        return this.getDisValue(20, 'shipTypeAIS', 'en_US')
      case 7:
        return this.getDisValue(21, 'shipTypeAIS', 'en_US')
      case 8:
        return this.getDisValue(22, 'shipTypeAIS', 'en_US')
      case 9:
        return this.getDisValue(23, 'shipTypeAIS', 'en_US')
    }
  }
  static getShipTypeCN(key: number): string {
    if (key === 100) {
      return '集装箱船'
    }
    if (key < 10 || key > 100) {
      return '其他'
    }

    const tensDigit = Math.floor(key / 10)
    const onesDigit = key % 10

    if (tensDigit === 5) {
      return ShipxyOptions.shipResourceManager.type[onesDigit]
    }

    if (tensDigit === 3) {
      return onesDigit >= 0 && onesDigit <= 7
        ? ShipxyOptions.shipResourceManager.type[onesDigit + 10]
        : '其他'
    }

    switch (tensDigit) {
      case 1:
      default:
        return '其他'
      case 2:
        return ShipxyOptions.shipResourceManager.type[18]
      case 4:
        return ShipxyOptions.shipResourceManager.type[19]
      case 6:
        return ShipxyOptions.shipResourceManager.type[20]
      case 7:
        return ShipxyOptions.shipResourceManager.type[21]
      case 8:
        return ShipxyOptions.shipResourceManager.type[22]
      case 9:
        return ShipxyOptions.shipResourceManager.type[23]
    }
  }
  static getShipTypeEN(key: number) {
    return this.getDisValueEN(key, 'shipTypeAIS', 'en_US')
  }
  static getNewShipTypeCN(key: number) {
    return CanvasShipUtils.getDisValue(key, 'shipType', 'zh_CN')
  }
}
